/**
 * Pure display-derivation helpers for the Profile page (specs/profile-page).
 * No hooks, no I/O — every value is derived from data the existing hooks already return,
 * so these are unit-testable in isolation (plan.md testing notes).
 */

import type { XpSummary } from '../../economy/helpers/xpApi';
import { getXpProgress } from '../../economy/helpers/rankRegistry.helper';
import type { ArenaStats } from '../../challenges/types/arenaStats.types';
import type { Friend } from '../../friends/types/friends.types';
import type { DisplayAvatarId } from '../../avatars/types/avatar.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';

export interface ProfileXpDisplay {
  level: number;
  levelNext: number;
  /** XP still needed to reach the next level (never negative). */
  xpToGo: number;
  /** Fill ratio 0..1 for the progress bar. */
  progress: number;
  rank: number;
}

/** Derive the XP-bar display values from the XP summary (AC-7). */
export function deriveXpDisplay(summary: XpSummary | null | undefined): ProfileXpDisplay | null {
  if (!summary) return null;
  const level = summary.level;
  const xpToGo = Math.max(0, summary.xpForLevel - summary.xpIntoLevel);
  return {
    level,
    levelNext: level + 1,
    xpToGo,
    progress: getXpProgress(summary.xpIntoLevel, summary.xpForLevel),
    rank: summary.rank,
  };
}

export interface ProfileStatTile {
  key: 'played' | 'won' | 'winRate' | 'streak';
  label: string;
  /** Pre-formatted display value (e.g. "58%"). */
  value: string;
}

/**
 * The four stat tiles: Played / Won / Win rate / Streak (AC-9, AC-10).
 * All values come straight from the existing Arena challenge stats — no new metric.
 * A null/absent stats object yields zeroes (the screen decides loading vs. error separately).
 */
export function deriveStatTiles(stats: ArenaStats | null | undefined): ProfileStatTile[] {
  const played = stats?.totalChallenges ?? 0;
  const won = stats?.wins ?? 0;
  const winRate = stats?.winRate ?? 0;
  const streak = stats?.currentStreak ?? 0;
  return [
    { key: 'played', label: 'Played', value: String(played) },
    { key: 'won', label: 'Won', value: String(won) },
    { key: 'winRate', label: 'Win rate', value: `${winRate}%` },
    { key: 'streak', label: 'Streak', value: String(streak) },
  ];
}

export interface HeadToHeadRow {
  id: string;
  name: string;
  wins: number;
  losses: number;
  /** "7–3" (en-dash), user's wins first. */
  record: string;
  /** Whether the viewer is ahead — drives the record colour. */
  ahead: boolean;
  avatarId: DisplayAvatarId | null;
  avatarConfig: EquippedMap | null;
  avatarUrl: string | null;
}

/**
 * Per-friend head-to-head rows from the friends list (AC-17, AC-18).
 * Only accepted friends who have actually played the viewer are included — a friend with no
 * completed challenges has no rivalry to show. No aggregate/overall number is produced.
 * Sorted by most games played, then most wins, so the strongest rivalries surface first.
 */
export function deriveHeadToHeadRows(friends: Friend[] | null | undefined): HeadToHeadRow[] {
  if (!friends || friends.length === 0) return [];
  return friends
    .filter((f) => f.friendshipStatus !== 'pending' && (f.wins + f.losses) > 0)
    .map((f) => ({
      id: f.id,
      name: f.name,
      wins: f.wins,
      losses: f.losses,
      record: `${f.wins}–${f.losses}`,
      ahead: f.wins > f.losses,
      avatarId: f.avatarId ?? null,
      avatarConfig: f.avatarConfig ?? null,
      avatarUrl: typeof f.avatar === 'string' && f.avatar.length > 0 ? f.avatar : null,
    }))
    .sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses) || b.wins - a.wins);
}
